import dayjs from 'dayjs'
import { Cookies } from 'quasar'

import { i18n as _i18n } from './boot/i18n'
import { ADD_TOKEN_TRANSACTION, NO_TOKEN_TRANSACTION, sendInfoException } from './modules/sentry-api'
import { offlineDb } from './offlineStorage'
import store from './store'

const apiUrl = () => localStorage.getItem('apiUrl')

const USE_ABORT_CONTROLLER = true

const STR = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
export const sentryDsn = process.env.SENTRY_DSN || ''

const i18n = _i18n.t('api')

const generateId = () => {
  function randomNumber (min = 0, max = 61) {
    return Math.floor(Math.random() * (max - min + 1)) + min
  }
  let res = '_'
  for (let i = 0; i < 8; i++) {
    res = res + STR[randomNumber()]
  }
  return res
}

const uriPrefix = (process.env.API || apiUrl() || '') + '/api/v1/'
const getUriPrefix = () => (process.env.API || apiUrl() || '') + '/api/v1/'
const commands = {
  'getCurrentTickPoint': {
    method: 'get',
    uriPath: uriPrefix + 'tick_points/current_tick_point/',
    path: 'tick_points/current_tick_point/'
  },
  'tokenAuth': {
    method: 'post',
    uriPath: uriPrefix + 'token-auth/',
    path: 'token-auth/'
  },
  'getWorkerDays': {
    method: 'get',
    uriPath: uriPrefix + 'worker_days/',
    storeMirrored: 'worker_days',
    path: 'worker_days/'
  },
  'initTick': {
    method: 'post',
    uriPath: uriPrefix + 'ticks/',
    storeAsImportant: 'offline_ticks',
    path: 'ticks/'
  },
  'updateTick': {
    method: 'put',
    uriPath: uriPrefix + 'ticks/{id}/',
    path: 'ticks/{id}/'
  },
  'setTickPhoto': {
    method: 'post',
    uriPath: uriPrefix + 'tick_photos/',
    storeAsImportant: 'offline_tick_photos',
    path: 'tick_photos/'
  },
  'getFilteredTicks': {
    method: 'get',
    uriPath: uriPrefix + 'ticks/',
    storeMirrored: 'filtered_ticks',
    path: 'ticks/'
  },
  'getTick': {
    method: 'get',
    uriPath: uriPrefix + 'ticks/',
    path: 'ticks/'
  }
}

let nowSendingOfflineTicks = false
const storedItems = []

const commandsImportant = Object.entries(commands)
  .filter((arr) => {
    return arr[1].storeAsImportant
  })
  .map(item => item[0])

const toQueryString = object =>
  '?' +
  Object.keys(object)
    .map(key => `${key}=${object[key].toString()}`)
    .join('&')

let TOKEN = localStorage.getItem('K2')
if (!TOKEN || TOKEN === 'undefined') {
  TOKEN = localStorage.getItem('IST') // !!!
  sendInfoException(NO_TOKEN_TRANSACTION)
}
if (!TOKEN || TOKEN === 'undefined') TOKEN = '' // !!!
Cookies.set('auth_token', TOKEN)

function HTTP (method, url, data = {}) {
  if (method === 'get') {
    url += toQueryString(data)
  }

  return new Promise((resolve, reject) => {
    let controller, abortTimeout

    if (USE_ABORT_CONTROLLER) { // we are using this controller because "fetch" has no timeout option
      controller = new AbortController()
      abortTimeout = setTimeout(() => {
        controller.abort()
      }, 45000)
    }

    fetch(url, {
      signal: USE_ABORT_CONTROLLER ? controller.signal : null,
      method,
      mode: 'cors', // no-cors, cors, *same-origin
      cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
      credentials: 'include', // include, *same-origin, omit
      headers: {
        'Authorization': 'Token ' + TOKEN
      },
      redirect: 'follow', // manual, *follow, error
      referrer: 'no-referrer', // no-referrer, *client
      body: method === 'get' ? null : data
    })
      .then(async response => {
        if (USE_ABORT_CONTROLLER) {
          clearTimeout(abortTimeout)
        }
        if (!response.ok) {
          let msg = i18n[response.status] || response.statusText // try to get message from i18n by the status code, default to english statusText
          throw new Error(msg)
        }

        const answer = await response.json()
        answer.http_status = response.status

        resolve(answer)
      })
      .catch((e) => {
        if (e.name === 'AbortError') console.warn('request aborted due to timeout:', url, method)
        reject(e)
      })
  })
}

export const API_REQUEST = async (cmd, req, justSend = false) => {
  if (!cmd) {
    throw new Error('"CMD" parameter (first) is missing.')
  }

  /*
  if (cmd === 'setTickPhoto') {
    const imageBlob = req.image
    const objectURL = URL.createObjectURL(imageBlob)
    window.open(objectURL, '_blank')
  }
  */
  const reqOptions = commands[cmd]
  if (!reqOptions) {
    throw new Error(`wrong command: "${cmd}"`)
  }

  let url = getUriPrefix() + reqOptions.path // reqOptions.uriPath

  if (req) {
    Object.keys(req).forEach(param => {
      const isHas = url.includes(`{${param}}`)
      if (isHas) {
        url = url.replace(`{${param}}`, req[param])
        if (cmd === 'updateTick' && param === 'id') { // TODO: improve this condition
          return
        }
        delete req[param]
      }
    })
  }

  let dataToSend

  switch (reqOptions.method) {
    case 'get':
      dataToSend = req
      break

    default: // "post" method or not "get" method
      const formData = new FormData()
      for (let key in req) {
        let value = req[ key ]
        if (Array.isArray(value)) value = '[' + value.toString + ']'
        formData.append(key, value)
      }
      // formData.append('csrfmiddlewaretoken', csrfToken)
      dataToSend = formData
      break
  }

  if (!justSend) {
    if (commandsImportant.includes(cmd)) {
      const dttm = dayjs().format('YYYY-MM-DDTHH:mm:ss') + 'Z'
      req.dttm = dttm // used only when non-first or offline query because dataToSend is already serialized
      const internalId = generateId()
      req.internalId = internalId
      const storeName = commands[cmd].storeAsImportant
      await offlineDb.addRecord(storeName, req)
      storedItems.push({
        storeName,
        id: internalId
      })
    }
  }

  const usualApiCall =
    Boolean(justSend) ||
    navigator.onLine ||
    !['initTick', 'setTickPhoto', 'getFilteredTicks', 'getWorkerDays'].includes(cmd)

  return new Promise(async (resolve, reject) => {
    switch (usualApiCall) {
      case true:
        HTTP(reqOptions.method,
          url,
          dataToSend
        )
          .then(async (res) => {
            if (cmd === 'tokenAuth') {
              localStorage.setItem('K2', res.token)
              TOKEN = res.token
              Cookies.set('auth_token', TOKEN)
              sendInfoException(ADD_TOKEN_TRANSACTION)
            }

            const storeMirrored = commands[cmd].storeMirrored
            if (storeMirrored && res.http_status === 200) {
              await offlineDb.clearAndFillStore(storeMirrored, res)
            }

            if (!justSend) {
              const storeAsImportant = commands[cmd].storeAsImportant
              if (storeAsImportant && storedItems.length) {
                const storedItem = storedItems.shift()
                await offlineDb.deleteRecord(storedItem.storeName, {
                  name: 'internalId',
                  value: storedItem.id
                })
              }
            }

            resolve(res)
          })
          .catch((e) => {
            const msg = e.detail || e.error || e
            if ((typeof msg === 'object' && !Object.keys(msg).length) || msg === '{}') {
              reject(e)
              return
            }
            store.commit('setErrorModal', {
              enabled: true,
              message: msg
            })
            reject(e)
          })
        break
      case false:
        resolve(await handleOnBadNetwork({ cmd, req }))
        break
    }
  })
}

async function handleOnBadNetwork ({ cmd, req }) {
  return new Promise(async (resolve, reject) => {
    const storeMirrored = commands[cmd].storeMirrored
    if (storeMirrored) {
      const res = await offlineDb.getAllRecords(storeMirrored)
      if (cmd === 'getWorkerDays') res.http_status = 200
      resolve(res)
    }

    if (cmd === 'initTick') {
      const localTickId = storedItems
        .reverse()
        .find((item) => item.storeName === 'offline_ticks')
        .id
      resolve({
        id: localTickId
      })
    }
    if (cmd === 'setTickPhoto') {
      const dttm = dayjs().format('YYYY-MM-DDTHH:mm:ss') + 'Z'
      const image = req.type === 'S' ? URL.createObjectURL(req.image) : null
      resolve({
        dttm,
        tick_id: req.tick_id,
        image,
        is_verified: 0,
        type: req.type,
        OFFLINE: true
      })

      if (req.type === 'S') { // let's add "virtual" offline tick
        await offlineDb.addRecord('filtered_ticks', {
          id: req.tick_id,
          dttm,
          is_verified: 1,
          user_id: req.user_id,
          type: req.tickType,
          OFFLINE: true
        })
      }
    }
  })
}

async function checkOfflineTicks () {
  if (!nowSendingOfflineTicks) {
    nowSendingOfflineTicks = true
    console.warn('Let\'s send offline records.')
    const offlineTicks = await offlineDb.getAllRecords('offline_ticks')
    console.warn('offline ticks amount: ', offlineTicks.length)
    for (let i = 0; i < offlineTicks.length; i++) {
      const oldTickId = offlineTicks[i].internalId
      const res = await API_REQUEST('initTick', offlineTicks[i], 'JUST_SEND')
      await offlineDb.deleteRecord('offline_ticks', {
        name: 'internalId',
        value: oldTickId
      })
      const newTickId = res.id
      await offlineDb.setNewIndexByOld('offline_tick_photos', {
        name: 'tick_id',
        oldValue: oldTickId,
        newValue: newTickId
      })
    }

    const offlinePhotosToSend = await offlineDb.filterByIndex('offline_tick_photos', {
      name: 'tick_id',
      filter: /^\d+$/ // "sendable" photo tick is a tick with non-generated id (_abcdef5), i.e: 4205
    })

    console.warn('offline photos amount: ', offlinePhotosToSend.length)
    for (let i = 0; i < offlinePhotosToSend.length; i++) {
      const internalId = offlinePhotosToSend[i].internalId
      delete offlinePhotosToSend[i].internalId
      delete offlinePhotosToSend[i].user_id
      delete offlinePhotosToSend[i].tickType
      await API_REQUEST('setTickPhoto', offlinePhotosToSend[i], 'JUST_SEND')
      await offlineDb.deleteRecord('offline_tick_photos', {
        name: 'internalId',
        value: internalId
      })
    }
    nowSendingOfflineTicks = false
    if (offlineTicks.length || offlinePhotosToSend.length) {
      window.dispatchEvent(new CustomEvent('onUpdateNeeded', {
        detail: { updateSubject: 'getFilteredTicks' }
      }))
    }
  }
}

window.addEventListener('online', async () => {
  checkOfflineTicks()
})

if (navigator.onLine) checkOfflineTicks()
